import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Header, Footer } from "../../components/Orchestrio";

const WebsitesPage = () => {
  return (
    <div className="min-h-screen bg-[#1E1E1E]" data-testid="websites-service-page">
      <Header />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/services" className="text-[#5F8FB8] text-sm hover:underline mb-6 inline-block">
            ← Back to Services
          </Link>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Websites
          </h1>
          <p className="text-xl text-[#5F8FB8] mb-4">
            High-performance, SEO-ready, conversion-focused
          </p>
          <p className="text-lg text-[#9CA3AF] max-w-2xl leading-relaxed">
            Built as maintainable systems, not disposable pages.
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">The problem we solve</h2>
          <div className="max-w-2xl">
            <p className="text-[#9CA3AF] leading-relaxed mb-4">
              Most business websites are built to look good on launch day—then quickly become 
              slow, outdated, and impossible to update without developer help.
            </p>
            <p className="text-[#9CA3AF] leading-relaxed mb-4">
              They're built with whatever framework was trendy that week, 
              with no thought for who will maintain them or how they'll perform under real traffic.
            </p>
            <p className="text-[#9CA3AF] leading-relaxed">
              The result: a site that loads slowly, ranks poorly, and becomes a liability 
              rather than an asset.
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">How we approach it</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold mb-2">Performance is non-negotiable</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Every site we build targets sub-2-second load times. We optimise images, 
                minimise JavaScript, and use proper caching. Fast sites rank better and convert better.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">SEO is built in, not bolted on</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Semantic HTML, proper heading structure, meta tags, schema markup, XML sitemaps. 
                The technical foundation for ranking well is included from day one.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">You can update it yourself</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                We set up a content management system that your team can actually use. 
                Add pages, update text, swap images—without calling a developer.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Standard technologies only</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                No obscure frameworks. No vendor lock-in. When you need to make changes later, 
                any competent developer will be able to work with what we've built.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">What you can expect</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-[#242424] border border-[#2E2E2E]">
              <h3 className="font-semibold mb-4">Deliverables</h3>
              <ul className="space-y-3">
                {[
                  "Custom responsive design",
                  "Content management system",
                  "SEO technical foundation",
                  "Analytics integration",
                  "Performance optimisation",
                  "Contact forms with CRM sync",
                  "SSL and security setup",
                  "Documentation"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                    <CheckCircle className="w-4 h-4 text-[#5F8FB8] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 bg-[#242424] border border-[#2E2E2E]">
              <h3 className="font-semibold mb-4">Timeline & Investment</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-[#6B7280] mb-1">Typical timeline</p>
                  <p className="font-semibold">4–8 weeks</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280] mb-1">Investment range</p>
                  <p className="font-semibold">£5,000 – £20,000 +VAT</p>
                </div>
                <div className="pt-4 border-t border-[#2E2E2E]">
                  <p className="text-xs text-[#6B7280]">
                    Price varies based on number of pages, custom functionality, 
                    and content migration requirements. Hosting and domains are separate.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Best For */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">This is right for you if...</h2>
          <ul className="space-y-3 max-w-2xl">
            {[
              "Your current site is slow, outdated, or embarrassing",
              "You need a site that ranks well and converts visitors",
              "You want your team to update content without developer help",
              "You've been burned by agencies who built something unmaintainable"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-[#9CA3AF]">
                <span className="text-[#5F8FB8]">→</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-3">Ready to discuss your website?</h2>
              <p className="text-[#9CA3AF] max-w-lg">
                Get in touch and we'll review your current site, understand your goals, 
                and give you honest advice on whether a rebuild makes sense.
              </p>
            </div>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2 flex-shrink-0">
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WebsitesPage;
